import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Crown, Eye, Flame, RefreshCw, Settings2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, errorText } from '../governance_v2/constants';
import { PriorityRow } from './PriorityRow';
import { BriefingSettingsDialog } from './BriefingSettingsDialog';
import '../materiality/materiality.css';

const base = (cid) => `${API}/companies/${cid}/attention`;
const when = (iso) => new Date(iso).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });

const BriefingCard = ({ companyId, briefing, onNavigate, userEmail }) => {
  const qc = useQueryClient();
  const seen = useMutation({
    mutationFn: () => axios.post(`${base(companyId)}/briefings/${briefing.id}/seen`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attention'] }),
  });
  const isSeen = briefing.seen_by?.includes(userEmail);
  return (
    <section className="rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-6" data-testid="briefing-card">
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <h3 className="mr-auto text-lg font-bold text-slate-900 dark:text-white">Briefing del CEO · {briefing.date}</h3>
        <span className="text-xs text-slate-500" data-testid="briefing-meta">{when(briefing.generated_at)} · {briefing.generated_by === 'system' ? 'automático' : 'manual'} · redacción {briefing.narrative_source === 'ia' ? 'SUPER-100' : 'determinista'}</span>
        <Button size="sm" variant="outline" disabled={isSeen || seen.isPending} onClick={() => seen.mutate()} data-testid="briefing-seen-btn">
          <Eye className="mr-1 h-3.5 w-3.5" />{isSeen ? 'Visto' : 'Marcar como visto'}</Button>
      </div>
      <p className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-slate-700 dark:text-slate-200" data-testid="briefing-narrative">{briefing.narrative}</p>
      <ol className="mt-4">{briefing.items.map((it, i) => <PriorityRow key={it.key} item={it} rank={i + 1} onNavigate={onNavigate} testid={`briefing-item-${i + 1}`} />)}</ol>
      {!briefing.items.length && <p className="mt-3 text-sm text-slate-500">Sin temas urgentes.</p>}
      {briefing.lessons?.length > 0 && (
        <div className="mt-4 rounded-xl border border-indigo-300/50 bg-indigo-500/[0.06] p-4" data-testid="briefing-lessons">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">Lecciones institucionales aplicables</p>
          <ul className="space-y-1.5">{briefing.lessons.map((l) => <li key={l.id} className="text-sm text-slate-700 dark:text-slate-200"><b>{l.title}:</b> {l.lesson}</li>)}</ul>
        </div>)}
    </section>
  );
};

const History = ({ companyId, value, onChange }) => {
  const { data } = useQuery({ queryKey: ['attention', 'history', companyId], queryFn: async () => (await axios.get(`${base(companyId)}/briefings`)).data.items });
  if (!data?.length) return null;
  return (
    <Select value={value || data[0].id} onValueChange={onChange}>
      <SelectTrigger className="w-64" data-testid="briefing-history"><Clock className="mr-2 h-4 w-4" /><SelectValue /></SelectTrigger>
      <SelectContent>{data.map((b) => <SelectItem key={b.id} value={b.id}>{when(b.generated_at)}{b.generated_by === 'system' ? '' : ' (manual)'}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export const AttentionPanel = ({ companyId, userEmail, onNavigate }) => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  useEffect(() => setSelected(null), [companyId]);
  const live = useQuery({ queryKey: ['attention', 'live', companyId], enabled: !!companyId, refetchInterval: 60000,
    queryFn: async () => (await axios.get(`${base(companyId)}/live`)).data });
  const briefing = useQuery({ queryKey: ['attention', 'briefing', companyId, selected], enabled: !!companyId,
    queryFn: async () => (await axios.get(selected ? `${base(companyId)}/briefings/${selected}` : `${base(companyId)}/briefings/latest`)).data.briefing });
  const generate = useMutation({
    mutationFn: async () => (await axios.post(`${base(companyId)}/briefings`)).data.briefing,
    onSuccess: () => { toast.success('Briefing generado'); setSelected(null); qc.invalidateQueries({ queryKey: ['attention'] }); },
    onError: (e) => toast.error(errorText(e)),
  });
  const d = live.data;
  return (
    <div className="space-y-6" data-testid="attention-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Crown className="h-7 w-7 text-amber-500" />
        <div className="mr-auto"><h2 className="text-2xl font-bold text-slate-900 dark:text-white">Prioridad Ejecutiva</h2>
          <p className="text-sm text-slate-500">SUPER-100 vigila la empresa cada 15 minutos y ordena lo que el CEO debe atender: materialidad, vencimientos, novedad y días sin atender.</p></div>
        <History companyId={companyId} value={selected} onChange={setSelected} />
        <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="briefing-settings-btn"><Settings2 className="mr-2 h-4 w-4" />Horario</Button>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending} data-testid="briefing-generate-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${generate.isPending ? 'animate-spin' : ''}`} />{generate.isPending ? 'Generando…' : 'Generar briefing ahora'}</Button>
      </div>
      {d?.immediate_count > 0 && (
        <section className="rounded-3xl border-2 border-red-500/60 bg-red-500/[0.06] p-5" data-testid="immediate-section">
          <h3 className="flex items-center gap-2 font-bold text-red-600"><Flame className="h-5 w-5" />Atención inmediata · {d.immediate_count} alerta(s) crítica(s) nueva(s) desde el último briefing</h3>
          <ol>{d.immediate.map((it, i) => <PriorityRow key={it.key} item={it} rank={i + 1} onNavigate={onNavigate} testid={`immediate-item-${i + 1}`} />)}</ol>
        </section>
      )}
      {briefing.isLoading ? <p className="text-sm text-slate-400">Cargando briefing…</p> : briefing.data
        ? <BriefingCard companyId={companyId} briefing={briefing.data} onNavigate={onNavigate} userEmail={userEmail} />
        : <p className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="briefing-empty">Aún no hay briefing. Se generará a la hora configurada, o pulsa "Generar briefing ahora".</p>}
      <section className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700" data-testid="live-ranking">
        <h3 className="font-semibold text-slate-900 dark:text-white">Ranking en vivo <span className="text-xs font-normal text-slate-500">({d?.total ?? '…'} temas abiertos, se actualiza cada minuto)</span></h3>
        <ol>{(d?.items || []).slice(0, 15).map((it, i) => <PriorityRow key={it.key} item={it} rank={i + 1} onNavigate={onNavigate} testid={`live-item-${i + 1}`} />)}</ol>
      </section>
      <BriefingSettingsDialog companyId={companyId} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

export const useAttentionSummary = (companyId, enabled = true) => useQuery({
  queryKey: ['attention', 'summary', companyId], enabled: !!companyId && enabled, refetchInterval: 60000,
  queryFn: async () => (await axios.get(`${base(companyId)}/summary`)).data,
});

export const AttentionBadge = ({ companyId }) => {
  const { data } = useAttentionSummary(companyId);
  if (!data?.immediate_count) return null;
  return <span className="on-solid ml-auto rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid="attention-badge">{data.immediate_count}</span>;
};

export const AttentionSummaryCard = ({ companyId, onOpen }) => {
  const { data } = useAttentionSummary(companyId);
  if (!data) return null;
  return (
    <button type="button" onClick={onOpen} className="mb-8 block w-full rounded-2xl border border-amber-400/40 bg-amber-500/[0.06] p-5 text-left transition-colors hover:bg-amber-500/[0.12]" data-testid="attention-summary-card">
      <div className="flex flex-wrap items-center gap-3">
        <Crown className="h-5 w-5 text-amber-500" />
        <p className="mr-auto font-semibold text-slate-900 dark:text-white">Prioridad Ejecutiva{data.briefing ? ` · briefing ${data.briefing.date}` : ''}</p>
        {data.immediate_count > 0 && <span className="on-solid rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white" data-testid="attention-summary-immediate">{data.immediate_count} atención inmediata</span>}
        <span className="text-xs text-slate-500">{data.total} temas abiertos</span>
      </div>
      <ol className="mt-3 space-y-1">{data.top.map((t, i) => <li key={t.key} className="truncate text-sm text-slate-700 dark:text-slate-200"><b className="mr-2 font-mono">{Math.round(t.attention)}</b>{t.title}</li>)}</ol>
    </button>
  );
};
